import { useEffect } from "react";
import { AppState } from "react-native";
import { Logger } from "@lookiero/sty-psp-logging";
import { useReconciliateCheckoutBooking } from "../../domain/checkoutBooking/react/useReconciliateCheckoutBooking";

const MAX_TIMEOUT_DELAY = 2_147_483_647;

interface ToDeadlineFunction {
  (checkoutBookingExpiresOn: Date | null | undefined): number | undefined;
}

/**
 * A checkout has no expiry until its booking is created, and an unparseable date
 * yields NaN, which would schedule an immediate timeout instead of none.
 */
const toDeadline: ToDeadlineFunction = (checkoutBookingExpiresOn) => {
  if (!checkoutBookingExpiresOn) {
    return undefined;
  }

  const deadline = checkoutBookingExpiresOn.getTime();

  return Number.isNaN(deadline) ? undefined : deadline;
};

interface UseReconciliateCheckoutBookingOnExpiryFunctionArgs {
  readonly checkoutBookingId: string | undefined;
  readonly checkoutBookingExpiresOn: Date | null | undefined;
  readonly checkoutId: string | undefined;
  readonly logger: Logger;
}

interface UseReconciliateCheckoutBookingOnExpiryFunction {
  (args: UseReconciliateCheckoutBookingOnExpiryFunctionArgs): void;
}

const useReconciliateCheckoutBookingOnExpiry: UseReconciliateCheckoutBookingOnExpiryFunction = ({
  checkoutBookingId,
  checkoutBookingExpiresOn,
  checkoutId,
  logger,
}) => {
  const [reconciliate] = useReconciliateCheckoutBooking({ checkoutBookingId, checkoutId, logger });
  const deadline = toDeadline(checkoutBookingExpiresOn);

  useEffect(() => {
    if (!checkoutBookingId || deadline === undefined) return;

    const delay = deadline - Date.now();

    if (delay <= 0) {
      reconciliate();

      return;
    }

    const timeout = setTimeout(reconciliate, Math.min(delay, MAX_TIMEOUT_DELAY));

    return () => clearTimeout(timeout);
  }, [checkoutBookingId, deadline, reconciliate]);

  useEffect(() => {
    if (!checkoutBookingId || deadline === undefined) return;

    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active" && Date.now() >= deadline) {
        reconciliate();
      }
    });

    return () => subscription.remove();
  }, [checkoutBookingId, deadline, reconciliate]);
};

export { useReconciliateCheckoutBookingOnExpiry };
